/**
 * Theme utility functions for consistent theme application across the application
 */

/**
 * Updates the iPhone status bar color based on theme
 * @param isDarkMode - Whether dark mode is enabled
 */
export function updateStatusBarColor(isDarkMode: boolean): void {
  const statusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
  const themeColorMeta = document.querySelector('meta[name="theme-color"]');

  if (statusBarMeta) {
    // iOS status bar style: 'default' (light bg), 'black' (dark bg), or 'black-translucent'
    statusBarMeta.setAttribute('content', isDarkMode ? 'black' : 'default');
  }

  if (themeColorMeta) {
    // Page backgrounds from styles/goals-theme.css (--g-bg).
    themeColorMeta.setAttribute('content', isDarkMode ? '#121816' : '#eef1ee');
  }
}

/**
 * Applies the theme to all elements in the document
 * @param isDarkMode - Whether dark mode is enabled
 */
export function applyThemeToAllElements(isDarkMode: boolean): void {
  // Update document root and body
  if (isDarkMode) {
    document.documentElement.classList.add('dark');
    document.documentElement.classList.remove('light');
    document.body.classList.add('dark');
    document.body.classList.remove('light');
  } else {
    document.documentElement.classList.remove('dark');
    document.documentElement.classList.add('light');
    document.body.classList.remove('dark');
    document.body.classList.add('light');
  }

  // Update iPhone status bar color
  updateStatusBarColor(isDarkMode);

  // Force a repaint with requestAnimationFrame for smooth transitions
  requestAnimationFrame(() => {
    // Temporarily disable transitions
    document.body.style.transition = 'none';
    document.documentElement.style.transition = 'none';

    // Force a reflow
    void document.body.offsetHeight;

    // Apply theme to all content elements for complete coverage
    const selectors = [
      '.blog-post-content',
      '.blog-post-detail',
      '.blog-container',
      'article',
      'main',
      '.blog-post',
      '.blog-post-footer',
      '.blog-post-header',
      '.admin-panel',
      '.blog-post-featured-image',
      '.blog-post-tags',
      '.blog-post-share',
      '.tag-button',
      '.share-button',
      'pre',
      'code',
      'blockquote'
    ];

    const contentElements = document.querySelectorAll(selectors.join(', '));
    contentElements.forEach(el => {
      if (el) {
        if (isDarkMode) {
          el.classList.add('dark');
          el.classList.remove('light');
        } else {
          el.classList.remove('dark');
          el.classList.add('light');
        }
      }
    });

    // Restore transitions after the next frame
    requestAnimationFrame(() => {
      document.body.style.transition = '';
      document.documentElement.style.transition = '';
    });
  });
}

/**
 * Gets the current theme from localStorage
 * @returns Whether dark mode is enabled
 */
export function getStoredTheme(): boolean {
  const savedTheme = localStorage.getItem('theme');
  const savedDarkMode = localStorage.getItem('darkMode');

  return !(savedTheme === 'light' || savedDarkMode === 'false');
}

/**
 * Saves the current theme to localStorage
 * @param isDarkMode - Whether dark mode is enabled
 */
export function saveThemePreference(isDarkMode: boolean): void {
  localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  localStorage.setItem('darkMode', isDarkMode ? 'true' : 'false');
}

/**
 * Whether the OS/browser is currently asking for a dark colour scheme.
 */
export function prefersDarkScheme(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * Whether the user has ever explicitly chosen a theme in this browser.
 * Used to decide between honouring the system preference and their override.
 */
export function hasStoredThemePreference(): boolean {
  return localStorage.getItem('theme') !== null
    || localStorage.getItem('darkMode') !== null
    || localStorage.getItem('dark_mode') !== null;
}

/**
 * Resolves the theme to use: an explicit stored choice wins, otherwise the
 * system preference.
 */
export function resolveInitialTheme(): boolean {
  if (hasStoredThemePreference()) {
    const stored = localStorage.getItem('theme');
    if (stored) return stored === 'dark';

    // Legacy keys.
    const legacy = localStorage.getItem('darkMode') ?? localStorage.getItem('dark_mode');
    return legacy === 'true';
  }

  return prefersDarkScheme();
}

/**
 * Applies the `light-mode`/`dark-mode` body classes the app's stylesheets key
 * off, and enables colour transitions after the first paint so the initial
 * render doesn't animate.
 */
export function applyThemeClasses(isDarkMode: boolean): void {
  const { body } = document;
  if (!body) return;

  body.classList.toggle('dark-mode', isDarkMode);
  body.classList.toggle('light-mode', !isDarkMode);
  document.documentElement.classList.toggle('dark-mode', isDarkMode);
  document.documentElement.classList.toggle('light-mode', !isDarkMode);

  updateStatusBarColor(isDarkMode);

  // Only transition on subsequent changes, never the first paint.
  if (!body.classList.contains('theme-transition')) {
    requestAnimationFrame(() => body.classList.add('theme-transition'));
  }
}

/* ---------------------------------------------------------------------------
 * Blueprint (marketing site) theme
 * ---------------------------------------------------------------------------
 * Separate from the goals-app theme above: the marketing site keys off a
 * `bp-light` class on <html> and its own `bp-theme` storage key, so the two
 * themes can never fight over the same class or localStorage entry.
 * ------------------------------------------------------------------------- */

const BP_STORAGE_KEY = 'bp-theme';

export type BlueprintTheme = 'light' | 'dark';

/** An explicit choice if the visitor has made one, otherwise the OS setting. */
export function resolveBlueprintTheme(): BlueprintTheme {
  try {
    const stored = localStorage.getItem(BP_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // Private mode / storage disabled — fall through to the system preference.
  }
  return prefersDarkScheme() ? 'dark' : 'light';
}

export function hasStoredBlueprintTheme(): boolean {
  try {
    const stored = localStorage.getItem(BP_STORAGE_KEY);
    return stored === 'light' || stored === 'dark';
  } catch {
    return false;
  }
}

export function storeBlueprintTheme(theme: BlueprintTheme): void {
  try {
    localStorage.setItem(BP_STORAGE_KEY, theme);
  } catch {
    // Not fatal: the theme still applies for this page view.
  }
}

/**
 * Applies the theme to <html>. Transitions are enabled one frame after the
 * first call so the initial paint never animates.
 */
export function applyBlueprintTheme(theme: BlueprintTheme): void {
  const root = document.documentElement;
  if (!root) return;

  root.classList.toggle('bp-light', theme === 'light');
  root.style.colorScheme = theme;

  const themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (themeColorMeta) {
    themeColorMeta.setAttribute('content', theme === 'dark' ? '#0a0a0a' : '#ffffff');
  }

  if (!root.classList.contains('bp-theme-transition')) {
    requestAnimationFrame(() => root.classList.add('bp-theme-transition'));
  }
}
